import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, LogIn, ShoppingCart } from 'lucide-react';
import { authService } from '@/services/auth.service';

export function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authService.login(username, password);
      navigate('/');
    } catch (err: any) {
      const isNetwork =
        !err.response &&
        (err.code === 'ERR_NETWORK' ||
          err.message === 'Network Error' ||
          err.code === 'ECONNABORTED');
      if (isNetwork) {
        setError(
          'No hay conexión con la API (puerto 3001). Abre otra terminal, ve a apps\\api y ejecuta: npm run dev',
        );
      } else {
        setError(err.response?.data?.message || 'Error al iniciar sesión');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] p-4">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient orbs */}
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-indigo-500/10 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-blue-500/5 to-indigo-500/5 blur-3xl" />

        {/* Grid pattern */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.03]">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div className="w-full max-w-sm relative z-10">
        {/* Logo & Branding */}
        <div className="text-center mb-10">
          <div className="bg-white p-4 rounded-full shadow-2xl shadow-black/30 w-48 h-48 mx-auto mb-6 flex items-center justify-center border border-white/20">
            <img src="kaja-360-logo.png" alt="Kaja 360 POS" className="w-full h-auto object-contain" />
          </div>
        </div>

        {/* Login form */}
        <form onSubmit={handleSubmit} className="bg-white/95 backdrop-blur-xl rounded-2xl p-7 shadow-2xl shadow-black/20 space-y-5 border border-white/10">
          {/* Header with decorative line */}
          <div className="text-center mb-1">
            <div className="flex items-center gap-3 justify-center">
              <div className="h-px w-8 bg-gradient-to-r from-transparent to-blue-300" />
              <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
              <div className="h-px w-8 bg-gradient-to-l from-transparent to-blue-300" />
            </div>
            <p className="text-xs text-gray-400 mt-2 font-medium">ACCESO AL SISTEMA</p>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50/90 border border-red-200 text-red-600 text-sm p-3.5 rounded-xl flex items-center gap-2.5 animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Username */}
          <div className="space-y-1.5">
            <label htmlFor="username" className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Usuario</label>
            <div className="relative group">
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/20 to-indigo-500/20 opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 blur-sm" />
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Tu nombre de usuario"
                className="relative w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 outline-none transition-all duration-200 focus:bg-white focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10"
                autoFocus
                required
              />
            </div>
          </div>

          {/* Password */}
          <div className="space-y-1.5">
            <label htmlFor="password" className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Contraseña</label>
            <div className="relative group">
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/20 to-indigo-500/20 opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 blur-sm" />
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Tu contraseña"
                className="relative w-full px-4 py-3 pr-11 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 outline-none transition-all duration-200 focus:bg-white focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-4 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 disabled:from-gray-300 disabled:to-gray-300 disabled:text-gray-500 transition-all duration-200 shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 active:scale-[0.98] flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Iniciando sesión...</span>
              </>
            ) : (
              <>
                <LogIn size={18} />
                <span>Iniciar Sesión</span>
              </>
            )}
          </button>

          {/* Footer hint */}
          <p className="text-center text-[11px] text-gray-400 pt-1">
            Presiona <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded text-[10px] font-mono font-bold text-gray-500">Enter</kbd> para iniciar sesión
          </p>
        </form>

        {/* Version */}
        <p className="text-center text-xs text-blue-200/40 mt-6 font-medium tracking-wider">
          KAJA 360 v0.1.0
        </p>
      </div>

      {/* ── Full-screen loading overlay ── */}
      {loading && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#0F172A]/95 backdrop-blur-sm animate-in fade-in duration-300">
          {/* Company logo */}
          <div className="bg-white p-4 rounded-full shadow-2xl shadow-black/30 w-40 h-40 mx-auto mb-8 flex items-center justify-center border border-white/20">
            <img src="/kaja-360-logo.png" alt="Kaja 360 POS" className="w-full h-auto object-contain" />
          </div>

          {/* Animated rings */}
          <div className="relative mb-8">
            {/* Outer ring */}
            <div className="w-24 h-24 rounded-full border-2 border-blue-500/20 animate-[spin_3s_linear_infinite]" />
            {/* Middle ring */}
            <div className="absolute inset-1 w-22 h-22 rounded-full border-2 border-transparent border-t-indigo-400 border-r-indigo-400/50 animate-[spin_2s_linear_infinite_reverse]" />
            {/* Inner ring */}
            <div className="absolute inset-3 w-[72px] h-[72px] rounded-full border-2 border-transparent border-t-blue-400 animate-[spin_1.5s_linear_infinite]" />
            {/* Center icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
                <ShoppingCart size={24} className="text-white" />
              </div>
            </div>
          </div>

          {/* Loading text with dots animation */}
          <div className="text-center">
            <p className="text-lg font-bold text-white mb-1">Iniciando sesión</p>
            <p className="text-sm text-blue-200/60">
              <span className="inline-flex gap-1.5">
                <span className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '200ms' }} />
                <span className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '400ms' }} />
              </span>
            </p>
          </div>

          {/* Progress bar at bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/5 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-500 via-indigo-400 to-blue-500 animate-pulse rounded-full transition-all duration-1000" style={{ width: '40%', marginLeft: '30%' }} />
          </div>
        </div>
      )}
    </div>
  );
}
